import React, { MouseEventHandler, useEffect, useState } from "react";
import TextInput from "../common/TextInput";
import Select from "react-select";
import {
  MapMarker,
  Organization,
  Video,
  VideoCataloguesDto,
} from "../common/ObjectTypes";
import ManageMapMarkersPage from "../mapMarkers/ManageMapMarkersPage";
import ManageAddEditVideo from "../video/ManageAddEditVideo";
import VideoManagementPage from "../video/VideoManagementPage";
import MultiStepForm from "../common/MultiStepForm";
import { MoonLoader } from "react-spinners";
import { getCurrentUser } from "../../helpers/authHeader";
import SelectInput from "../common/SelectInput";
import { getWildXROrgId } from "../../helpers/wildXROrgIdHelper";

type AddEditCatalogueProps = {
  catalogue: VideoCataloguesDto;
  selectMapMarkersPage?: boolean;
  selectVideosPage?: boolean;
  existingCatalogue: boolean;
  mapMarkers: MapMarker[];
  selectedVideos: Video[];
  organizations: Organization[];
  onChange: Function;
  onChangeThumbnail: Function;
  onCancel: Function;
  onSave: MouseEventHandler;
  onChangeMapMarker: Function;
  onSelectVideoForCatalogue: Function;
  onSelectAllMapMarkers: Function;
  onSelectAllVideos: Function;
  areAllMapMarkersSelected?: boolean;
  areAllVideosSelected?: boolean;
  changesMade: boolean;
  errors: any;
  saving: boolean;
  isLoading: boolean;
  onChangeSingleSelect: Function;
};

const AddEditCatalogue = ({
  selectMapMarkersPage,
  selectVideosPage,
  catalogue,
  onChange,
  onSave,
  errors,
  selectedVideos,
  organizations,
  onChangeMapMarker,
  onSelectVideoForCatalogue,
  onSelectAllMapMarkers,
  onSelectAllVideos,
  areAllMapMarkersSelected,
  areAllVideosSelected,
  existingCatalogue,
  changesMade,
  saving,
  isLoading,
  onChangeSingleSelect,
}: AddEditCatalogueProps) => {
  const currentUser = getCurrentUser();
  const wildXROrgId = getWildXROrgId();

  //get mapMarkers
  const catalogueMapMarkers = catalogue.mapMarkers;

  // Function to check if a mapMarker is included in any of the mapMarkers
  const isMapMarkerIncluded = (mapMarkerId: string): boolean => {
    return catalogueMapMarkers.some(
      (mapMarker: MapMarker) => mapMarker.mapMarkerId === mapMarkerId
    );
  };

  //get videos
  const catalogueVideos = catalogue.videos;
  const isVideoIncluded = (videoId: string): boolean => {
    return (
      catalogue.videos?.some((video) => video.videoId === videoId) ?? false
    );
  };

  //get organization by code
  const getOrganizationByCode = (code: any) => {
    return organizations.find((org) => org.organizationId === code);
  };
  const organizationId = catalogue.organizationId;
  const defaultOrg = getOrganizationByCode(organizationId);

  //multistep form handling for handling next and back and state persistance
  const [currentStep, setCurrentStep] = useState(0);
  const [readOnly, setReadOnly] = useState(false);

  const cannotAccessMultiStep =
    catalogue.organizationId === wildXROrgId &&
    !currentUser.isSystemAdmin &&
    !currentUser.organizations?.some(
      (org: any) => org.organizationId === wildXROrgId
    );
  const steps = [
    {
      label: existingCatalogue ? "Edit Video Catalogue" : "Add Video Catalogue",
    },
    { label: "Map Markers" },
    { label: "Videos" },
    { label: "Review & Save" },
  ];
  const handleNext = () => {
    setCurrentStep((prevStep) => Math.min(prevStep + 1, steps.length - 1));
  };
  useEffect(() => {
    if (cannotAccessMultiStep) {
      setCurrentStep(steps.length - 1);
      setReadOnly(true);
    } else {
      setReadOnly(currentStep === 3);
    }
  }, [currentStep, currentUser]);

  const handleBack = () => {
    setCurrentStep((prevStep) => Math.max(prevStep - 1, 0));
  };

  return (
    <div className="w-full my-6">
      {/* Title */}
      <h1 className="text-3xl font-bold text-center mb-6">
        {existingCatalogue ? "Edit Video Catalogue" : "Add Video Catalogue"}
      </h1>

      {/* Multi-step form indicator */}
      {!cannotAccessMultiStep && (
        <div className="mb-6">
          <MultiStepForm
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col items-center">
        {(currentStep === 0 || currentStep === 3) && (
          <div className="bg-white w-11/12 md:w-2/3 rounded-xl shadow-lg p-6">
            {/* Name */}
            <div className="mb-4">
              <TextInput
                name="name"
                label="Name*"
                placeholder="Enter Name"
                value={catalogue.name ?? ""}
                error={errors.name}
                disabled={readOnly}
                className="p-2 rounded shadow w-full"
                type="text"
                initialFocus={true}
                onChange={(e: any) => onChange(e)}
              />
            </div>

            {/* Overview */}
            <div className="mb-4">
              <TextInput
                name={"overview"}
                label={"Overview*"}
                placeholder={"Enter Overview..."}
                value={catalogue.description ?? ""}
                error={errors.overview}
                disabled={readOnly}
                className={"p-2 rounded shadow w-full"}
                type={"text"}
                initialFocus={true}
                onChange={(e: any) => onChange(e)}
                rows={4}
              />
            </div>

            {/* Organization */}
            <div className="mb-4">
              {currentUser.isSystemAdmin ||
              currentUser.organizations.length > 1 ? (
                <SelectInput
                  key={defaultOrg ? defaultOrg.organizationId : "default"}
                  name="organizationId"
                  label="Select Organization*"
                  options={organizations.map((org: any) => ({
                    value: org.organizationId,
                    label: org.name,
                  }))}
                  onChange={(e: any) =>
                    onChangeSingleSelect("organizationId", e)
                  }
                  className="w-full"
                  defaultValue={
                    catalogue !== null && defaultOrg
                      ? {
                          value: defaultOrg.organizationId,
                          label: defaultOrg.name,
                        }
                      : undefined
                  }
                  isDisabled={readOnly}
                  isLoading={false}
                  isClearable={true}
                  isSearchable={true}
                  isMulti={false}
                  errors={errors.organizationId}
                />
              ) : (
                currentUser.organizations.map((org: any) => (
                  <div className="py-2 font-medium" key={org.organizationId}>
                    <span className="text-gray-600">Organization:</span>{" "}
                    {catalogue.organizationId === wildXROrgId
                      ? "WildXR"
                      : org.name}
                  </div>
                ))
              )}
            </div>

            {/* Read-only sections */}
            {readOnly && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-semibold mb-2">Map Markers</h2>
                  <div className="bg-grayScale-200 rounded p-3">
                    {catalogueMapMarkers.map((mapMarker: any) => (
                      <p key={mapMarker.mapMarkerId} className="text-gray-800">
                        {mapMarker.name}
                      </p>
                    ))}
                  </div>
                </div>

                <div>
                  <h2 className="text-lg font-semibold mb-2">Videos</h2>
                  <div className="bg-grayScale-200 rounded p-3">
                    {catalogueVideos.length || 0}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {currentStep === 1 && (
          <ManageMapMarkersPage
            selectMapMarkersPage
            onSelectMapMarkerForCatalogue={onChangeMapMarker}
            isCatalogueMapMarkerIncluded={isMapMarkerIncluded}
            onSelectAllMapMarkers={onSelectAllMapMarkers}
            areAllMapMarkersSelected={areAllMapMarkersSelected}
            isLoadingForCatalogueSelectAll={isLoading}
          />
        )}

        {currentStep === 2 && (
          <VideoManagementPage
            selectVideosPage
            catalogueMapMarkers={catalogueMapMarkers}
            onSelectVideoForCatalogue={onSelectVideoForCatalogue}
            isCatalogueVideoIncluded={isVideoIncluded}
            onSelectAllVideos={onSelectAllVideos}
            areAllVideosSelected={areAllVideosSelected}
            isLoadingForCatalogueSelectAll={isLoading}
          />
        )}
      </div>

      {/* Footer navigation buttons */}
      {!cannotAccessMultiStep && (
        <div className="flex justify-center mt-6">
          <div className="flex justify-between w-11/12 md:w-2/3">
            <button
              className="px-4 py-2 bg-deepGreen-500 text-white rounded-lg disabled:opacity-50"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              Back
            </button>

            {currentStep < steps.length - 1 ? (
              <button
                className="px-4 py-2 bg-deepGreen-500 text-white rounded-lg"
                onClick={handleNext}
              >
                Next
              </button>
            ) : saving ? (
              <div className="flex items-center">
                <MoonLoader color="#9EB45B" size={24} />
              </div>
            ) : (
              <button
                onClick={onSave}
                className={`px-4 py-2 rounded-lg ${
                  !changesMade
                    ? "bg-green-200 text-gray-600"
                    : "bg-deepGreen-500 text-white"
                }`}
                disabled={!changesMade}
              >
                Save
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEditCatalogue;
